import useCommon from '@/composables/useCommon'
import useFirestoreCollectionNames from '@/composables/useFirestoreCollectionNames'
import { ref, watch } from '@vue/composition-api'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { filter } from 'lodash'
import moment from 'moment'
import useMoment from './../../../../composables/useMoment'
import { db } from '/src/firebase/config'

export default function useSubscriptionsList() {
  const { stringContainsAny } = useCommon()
  const { subscriptionsCollectionName } = useFirestoreCollectionNames()

  const subscriptionListTable = ref([])
  const filteredSubscriptionListTable = ref([])

  const tableColumns = [
    { text: '#', value: 'srno', sortable: false },
    { text: 'Email', value: 'emailid' },
    { text: 'City', value: 'city' },
    { text: 'Subscription Date', value: 'createdon' },
  ]

  const dateOptions = ['Today', 'Last 3 Days', 'Last 7 Days', 'Last Month', 'Custom']
  const dateFilter = ref('Last 3 Days')

  const searchQuery = ref('')
  const loading = ref(false)
  let unsubscribe = null
  const options = ref({
    sortBy: ['createdon'],
    sortDesc: [true],
  })
  const dateRange1 = ref([])

  const queryOptions = () => {
    const qOptions = []

    const orderByKey = options.value.sortBy[0] ? options.value.sortBy[0] : 'createdon'
    const order = options.value.sortDesc[0] ? 'desc' : 'asc'
    qOptions.push(orderBy(orderByKey, order))

    return qOptions
  }

  // fetch data
  const fetchSubscriptions = async () => {
    try {
      if (unsubscribe) {
        unsubscribe()
      }
      loading.value = true
      subscriptionListTable.value = []
      filteredSubscriptionListTable.value = []

      const q = query(collection(db, subscriptionsCollectionName.value), ...queryOptions())
      unsubscribe = onSnapshot(q, querySnapshot => {
        subscriptionListTable.value = []
        querySnapshot.forEach(doc => {
          subscriptionListTable.value.push({
            id: doc.id,
            ...doc.data(),
          })
        })
        filteredSubscriptionListTable.value = subscriptionListTable.value
        filterSubscriptions()
      })
    } catch (e) {
      console.log(e)
    } finally {
      loading.value = false
    }
  }

  const filterSubscriptions = () => {
    let filteredSubscriptions = subscriptionListTable.value
    if (searchQuery.value && searchQuery.value.length) {
      filteredSubscriptions = filter(filteredSubscriptions, ({ emailid, city }) => {
        return stringContainsAny(emailid + city, searchQuery.value)
      })
    }

    if (dateRange1.value && dateRange1.value.length) {
      const { isBetweenDays, isSameDay } = useMoment()
      const [startDate, endDate] = dateRange1.value
      filteredSubscriptions = filter(filteredSubscriptions, ({ createdon }) => {
        if (endDate) {
          return isBetweenDays(createdon.toDate(), startDate, endDate)
        } else {
          return isSameDay(createdon.toDate(), startDate)
        }
      })
    }

    filteredSubscriptionListTable.value = filteredSubscriptions
  }

  watch([searchQuery], () => {
    filterSubscriptions()
  })

  watch([dateRange1], () => {
    if (dateRange1.value && dateRange1.value.length == 2) {
      const { isDateAfter } = useMoment()
      if (isDateAfter(dateRange1.value[0], dateRange1.value[1])) {
        ;[dateRange1.value[0], dateRange1.value[1]] = [dateRange1.value[1], dateRange1.value[0]]
      }
    }
    filterSubscriptions()
  })

  watch(
    [dateFilter],
    () => {
      dateRange1.value = []
      const startDate = moment().format('YYYY-MM-DD')
      switch (dateFilter.value) {
        case 'Today':
          dateRange1.value = [startDate]
          break
        case 'Last 3 Days':
          dateRange1.value = [startDate, moment().subtract(3, 'd').format('YYYY-MM-DD')]
          break
        case 'Last 7 Days':
          dateRange1.value = [startDate, moment().subtract(7, 'd').format('YYYY-MM-DD')]
          break
        case 'Last Month':
          dateRange1.value = [startDate, moment().subtract(30, 'd').format('YYYY-MM-DD')]
          break
      }
    },
    { immediate: true },
  )

  const unsubscribeSubscriptions = () => {
    unsubscribe()
  }

  fetchSubscriptions()

  return {
    subscriptionListTable,
    filteredSubscriptionListTable,
    tableColumns,
    searchQuery,
    loading,
    options,
    dateOptions,
    dateFilter,
    dateRange1,

    unsubscribeSubscriptions,
  }
}
